import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { useCSuiteAuth } from "../../state/CSuiteAuthContext";
import { api } from "../../lib/api";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString();

export default function CSuiteProjectView() {
  const { user } = useCSuiteAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get(id)
      .then(setDoc)
      .catch(e => setErr(e?.message || "Failed to load"))
      .finally(() => setBusy(false));
  }, [id]);

  if (busy) {
    return <div className="min-h-screen grid place-items-center bg-[var(--paper)]"><Loader2 size={32} className="animate-spin text-[var(--navy)]"/></div>;
  }
  if (err || !doc) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--paper)]">
        <div className="text-red-700 text-center">
          <AlertTriangle size={24} className="mx-auto mb-2"/>
          <div>{err || "Project not found"}</div>
          <Link to="/csuite/dashboard" className="btn btn-primary mt-4">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const meta = doc.meta || {};
  const tasks = doc.tasks || [];
  const budget = doc.budget || [];
  const raids = doc.raids || [];
  const s4 = doc.s4hana || {};
  const avgPct = tasks.length ? Math.round(tasks.reduce((s, t) => s + (t.pct || 0), 0) / tasks.length) : 0;
  const alloc = budget.reduce((s, b) => s + (b.allocated || 0), 0);
  const spent = budget.reduce((s, b) => s + (b.actual || 0), 0);
  const fcst  = budget.reduce((s, b) => s + (b.forecast || 0), 0);
  const ebitda = (s4.valueKpis || []).find(v => /ebitda/i.test(v.label || ""));
  const constraints = raids.filter(r => ["Critical", "High"].includes(r.impact) && r.status !== "Closed");

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--tx1)]" data-testid="csuite-project-view">
      <header className="border-b border-[var(--border)] bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/csuite/dashboard")} data-testid="csuite-back">
            <ArrowLeft size={13}/> Back to rollup
          </button>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 rounded-sm bg-[var(--navy)] text-white font-mono text-[10px] uppercase tracking-wider">{user?.role}</span>
            <Link to="/csuite/dashboard" className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-800">Dashboard</Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{meta.code}</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 mt-1">{meta.name}</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">
            {meta.phase} · Sponsor: {meta.sponsor || "—"} · PM: {meta.pm || "—"} · {meta.start} → {meta.end}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <Card label="Health" value={meta.health || "—"} sub="Current status"/>
          <Card label="Progress" value={`${avgPct}%`} sub={`${tasks.length} tasks`}/>
          <Card label="EBITDA Impact" value={ebitda?.value || "—"} sub={ebitda?.sub || "From S/4HANA tab"}/>
          <Card label="ROI" value={meta.roi || "—"} sub="From project meta"/>
        </div>

        <Section title="Financials">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Allocated" value={fmtMoney(alloc)} sub="Budget"/>
            <Card label="Actual"    value={fmtMoney(spent)} sub={`${alloc ? Math.round((spent / alloc) * 100) : 0}% of allocated`}/>
            <Card label="Forecast"  value={fmtMoney(fcst)}  sub="Projected"/>
            <Card label="Variance"  value={fmtMoney(fcst - alloc)} sub={fcst - alloc <= 0 ? "Under" : "Over"}/>
          </div>
        </Section>

        <Section title="Constraints (Critical / High)">
          <div className="bg-white border border-[var(--border)] rounded-sm" data-testid="csuite-project-constraints">
            {constraints.length === 0 && <div className="p-6 text-center text-zinc-500 italic text-sm">No active critical or high constraints</div>}
            {constraints.map((c, i) => (
              <div key={i} className="border-b border-[var(--border)] last:border-0 p-3 flex items-start gap-3">
                <div className={`w-1 self-stretch rounded-sm ${c.impact === "Critical" ? "bg-red-600" : "bg-amber-500"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-zinc-500">{c.type} · {c.ref}</div>
                  <div className="text-sm text-zinc-900 mt-0.5 font-medium">{c.title}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">Owner: {c.owner} · Status: {c.status}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {(s4.valueKpis || []).length > 0 && (
          <Section title="Value Realization Snapshot (from S/4HANA tab)">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(s4.valueKpis || []).map((v, i) => (
                <Card key={i} label={v.label} value={v.value} sub={v.sub}/>
              ))}
            </div>
          </Section>
        )}
      </main>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="bg-white border border-[var(--border)] rounded-sm p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-extrabold tracking-tighter mt-1 text-zinc-900">{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display text-lg font-bold tracking-tight text-zinc-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}
